import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { create } from "zustand";
import { toast } from "sonner";
import { Plus, Check, ListMusic, Lock, Disc3 } from "lucide-react";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export const usePlaylistDialog = create((set) => ({ track: null, open: (track) => set({ track }), close: () => set({ track: null }) }));

export const CollectionCover = ({ url, covers = [], className = "" }) => (
  <div className={`relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-[var(--hub)]/40 to-zinc-900 border border-white/[0.06] ${className}`}>
    {covers.length >= 4 ? (
      <div className="grid grid-cols-2 w-full h-full group-hover:scale-[1.04] transition-transform duration-500">{covers.slice(0, 4).map((c) => <img key={c} src={mediaUrl(c)} alt="" className="w-full h-full object-cover" loading="lazy" />)}</div>
    ) : url ? <img src={mediaUrl(url)} alt="" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" loading="lazy" />
      : <div className="w-full h-full flex items-center justify-center"><ListMusic size={40} className="text-white/40" /></div>}
  </div>
);

export const CollectionCard = ({ item, kind }) => {
  const id = item[`${kind}_id`];
  return (
    <Link to={`/${kind}/${id}`} data-testid={`${kind}-card-${id}`} className="group block reveal">
      <div className="relative">
        <CollectionCover url={item.cover_url} covers={item.covers} />
        {kind === "album" && <div className="absolute -right-2 top-2 bottom-2 w-4 rounded-r-full bg-zinc-800 -z-10 group-hover:-right-4 transition-[right] duration-300" />}
      </div>
      <div className="mt-3 font-semibold truncate">{item.title}</div>
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
        {kind === "album" ? <Disc3 size={11} /> : <ListMusic size={11} />}{kind === "album" ? item.type : item.owner?.name || "Playlist"} · {item.track_count} tracks
        {item.is_public === false && <Lock size={11} />}
      </div>
    </Link>
  );
};

export const AddToPlaylistDialog = () => {
  const { track, close } = usePlaylistDialog();
  const { user } = useAuth();
  const [lists, setLists] = useState([]);
  const [title, setTitle] = useState("");
  const load = () => api.get("/me/playlists").then((r) => setLists(r.data)).catch(() => {});
  useEffect(() => {
    if (!track) return;
    if (user) load();
    else { toast.error("Sign in to save tracks to playlists"); close(); }
  }, [track, user, close]);
  if (!user) return null;
  const add = async (pl) => {
    try {
      const { data } = await api.post(`/playlists/${pl.playlist_id}/tracks`, { track_id: track.track_id });
      toast.success(data.added ? `Added to “${pl.title}”` : `Already in “${pl.title}”`);
      close();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const createAndAdd = async () => {
    try {
      const { data } = await api.post("/playlists", { title, is_public: true, track_ids: [track.track_id] });
      toast.success(`Created “${data.title}”`);
      setTitle("");
      close();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!track} onOpenChange={(o) => !o && close()}>
      <DialogContent className="bg-[#12121A] border-white/10" data-testid="add-to-playlist-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Add to playlist</DialogTitle>
          <DialogDescription className="truncate">“{track?.title}” · {track?.artist?.name}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input data-testid="new-playlist-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New playlist name" className="bg-white/5 border-white/10" onKeyDown={(e) => e.key === "Enter" && title.trim() && createAndAdd()} />
          <button data-testid="new-playlist-create-button" disabled={!title.trim()} onClick={createAndAdd} className="px-4 rounded-md bg-[var(--hub)] text-white text-sm font-semibold disabled:opacity-40 inline-flex items-center gap-1"><Plus size={14} /> Create</button>
        </div>
        <div className="max-h-72 overflow-auto -mx-2" data-testid="my-playlists-list">
          {lists.length === 0 && <div className="px-2 py-4 text-sm text-zinc-500">No playlists yet — create your first above.</div>}
          {lists.map((pl) => {
            const has = pl.track_ids.includes(track?.track_id);
            return (
              <button key={pl.playlist_id} data-testid={`playlist-option-${pl.playlist_id}`} onClick={() => add(pl)} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 text-left">
                <div className="w-11 h-11 shrink-0"><CollectionCover url={pl.cover_url} covers={pl.covers} className="rounded-lg" /></div>
                <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{pl.title}</div><div className="text-xs text-zinc-500">{pl.track_count} tracks · {pl.is_public ? "Public" : "Private"}</div></div>
                {has ? <Check size={16} className="text-emerald-400" /> : <Plus size={16} className="text-zinc-400" />}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
